import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { useConfig, useDebouncedCallback } from '../../hooks';
import { NETWORK_QUALITY_EMBEDDED, NETWORK_QUALITY_FULLSCREEN } from '../../utilities';
import Button from '../Button';
import NoConnectionIcon from '../Icons/NoConnection';
import WarningIcon from '../Icons/Warning';
import WifiIcon from '../Icons/Wifi';
import Preloader from '../Preloader';
import SvgIcon from '../SvgIcon';

const NetworkQuality = ({
  isSlowConnection = false,
  isOnline = true,
  mode = NETWORK_QUALITY_FULLSCREEN,
  onRetry
}) => {
  const {
    fontFamily,
    button,
    window: { colors }
  } = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  /**
   * 1000ms debounce instaed Store Context RSF-Pattern dispatching.
   */
  const handleRetry = useDebouncedCallback(async () => {
    if (onRetry) {
      await onRetry();
    }

    setIsLoading(false);
  }, 1000);

  const handleClick = () => {
    setIsLoading(true);
    handleRetry();
  };

  if (isSlowConnection) {
    return (
      <Root mode={mode} backgroundColor={colors.background} textFontFamily={fontFamily}>
        {(() => {
          if (mode === NETWORK_QUALITY_FULLSCREEN) {
            return (
              <>
                <Heading>
                  <IconAfter>
                    <WarningIcon fill={colors.error} />
                  </IconAfter>
                  <BlockIcon>
                    <WifiIcon fill={colors.additional} />
                  </BlockIcon>
                  {t('connectionIssue')}
                </Heading>
                <Descr>{t('messageSlowConnection')}</Descr>
                <Button
                  {...button}
                  disabled={isLoading}
                  onClick={handleClick}
                  textFontFamily={button.fontFamily}
                >
                  {isLoading ? <Preloader /> : t('retry')}
                </Button>
              </>
            );
          }

          if (mode === NETWORK_QUALITY_EMBEDDED) {
            return (
              <>
                <Title textColor={colors.error}>
                  <Icon fill={colors.error}>
                    <WarningIcon />
                  </Icon>
                  {t('connectionIssue')}
                </Title>
                <Description>
                  {t('apology')} &#128148;. &nbsp; {t('messageSlowConnection')}
                </Description>
                <Button
                  {...button}
                  disabled={isLoading}
                  onClick={handleClick}
                  textFontFamily={button.fontFamily}
                >
                  {isLoading ? <Preloader /> : t('retry')}
                </Button>
              </>
            );
          }

          return null;
        })()}
      </Root>
    );
  }

  /**
   * Unmount on online.
   */
  if (isOnline) {
    return null;
  }

  /**
   * Return next if offline.
   */
  return (
    <Root mode={mode} backgroundColor={colors.background} textFontFamily={fontFamily}>
      {(() => {
        if (mode === NETWORK_QUALITY_FULLSCREEN) {
          return (
            <>
              <Heading textColor={colors.main}>
                <BlockIcon>
                  <NoConnectionIcon fill={colors.additional} />
                </BlockIcon>
                {t('noInternet')}
              </Heading>
              <Descr textColor={colors.main}>{t('messageNoInternet')}</Descr>
              <Button
                {...button}
                disabled={isLoading}
                onClick={handleClick}
                textFontFamily={button.fontFamily}
              >
                {isLoading ? <Preloader /> : t('retry')}
              </Button>
            </>
          );
        }

        if (mode === NETWORK_QUALITY_EMBEDDED) {
          return (
            <>
              <Title>
                <Icon fill={colors.error}>
                  <NoConnectionIcon />
                </Icon>
                {t('noInternet')}
              </Title>
              <Description textColor={colors.main}>
                {t('apology')} &#128148;. &nbsp; {t('messageNoInternet')}
              </Description>
              <Button {...button} disabled={isLoading} onClick={handleClick}>
                {isLoading ? <Preloader /> : t('retry')}
              </Button>
            </>
          );
        }

        return null;
      })()}
    </Root>
  );
};

NetworkQuality.propTypes = {
  isOnline: PropTypes.bool.isRequired,
  isSlowConnection: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf([NETWORK_QUALITY_FULLSCREEN, NETWORK_QUALITY_EMBEDDED]),
  onRetry: PropTypes.func
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 20px;
  font-family: ${({ textFontFamily }) => textFontFamily};

  ${({ mode }) => {
    if (mode === NETWORK_QUALITY_FULLSCREEN) {
      return css`
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        justify-content: center;
        width: 100%;
        height: 100%;
        padding: 10px 20px;
        background-color: ${({ backgroundColor }) => `${backgroundColor}`};
      `;
    }

    return null;
  }}
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  font-family: inherit;
  font-size: 14px;
  font-weight: 900;
  line-height: 20px;
  text-align: center;
  color: ${({ textColor }) => `${textColor}`};
`;

const Icon = styled.i`
  ${SvgIcon} {
    width: 14px;
    height: 14px;
    margin-right: 6px;
    fill: ${({ fill }) => `${fill}`};
  }
`;

const Description = styled.div`
  margin-bottom: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  color: ${({ textColor }) => `${textColor}`};
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  font-family: inherit;
  font-size: 24px;
  font-weight: 700;
  line-height: 36px;
  color: ${({ textColor }) => `${textColor}`};
`;

const BlockIcon = styled.i`
  ${SvgIcon} {
    width: 60px;
    height: 48px;
  }
`;

const IconAfter = styled.i`
  transform: translate(40px, 10px);

  ${SvgIcon} {
    width: 18px;
    height: 16px;
  }
`;

const Descr = styled.div`
  margin-bottom: 16px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  color: ${({ textColor }) => `${textColor}`};
`;

export default NetworkQuality;
